import { useMemo } from 'react'

import { useCollaborationUpsell } from '@/collaboration/hooks/useCollaborationUpsell'
import { AvatarStack, type AvatarData } from '@/components/avatar-stack/AvatarStack'
import { Button } from '@/components/button/Button'
import { IconButton } from '@/components/icon-button/IconButton'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/popover/Popover'
import { AddCollaborator, Lock, ArrowUpRight } from '@/assets/icons'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { MattersActions, MatterDialogType } from '@/matter/store/slice'
import { selectActiveMatter } from '@/matter/store/selectors'
import { selectMatterMembers } from '@/collaboration/store/selectors'
import { getMemberInitials, getMemberDisplayName } from '@/collaboration/utils/members'
import { isUnassignedMatter } from '@/matter/utils/is-unassigned-matter'
import { canEditMatterMembers, canViewMatterMembers } from '@/matter/utils/matter-permissions'

function UpsellPopoverContent({ onClick }: { onClick: () => void }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold leading-5 tracking-[-0.15px] text-brand-neutral-950">Collaborate on this matter</p>
        <p className="text-sm leading-5 tracking-[-0.15px] text-brand-neutral-950">Invite colleagues to work on this matter with their own Paxton account.</p>
      </div>
      <div className="-mx-2">
        <Button variant="plain" color="primary" size="sm" trailingIcon={<ArrowUpRight className="h-3.5 w-3.5" />} onClick={onClick}>
          Create an organization
        </Button>
      </div>
    </div>
  )
}

export function CollaborationHeaderActions() {
  const { showUpsell, collaborationEnabled, handleUpsellClick } = useCollaborationUpsell()

  const dispatch = useAppDispatch()
  const matter = useAppSelector(selectActiveMatter)
  const disabled = isUnassignedMatter(matter)

  const rawMembers = useAppSelector((state) => selectMatterMembers(state, canViewMatterMembers(matter) ? matter?.id : undefined))
  const members: AvatarData[] = useMemo(
    () => rawMembers.map((member) => ({ userId: member.user_id, initials: getMemberInitials(member), name: getMemberDisplayName(member) })),
    [rawMembers]
  )

  if (!collaborationEnabled) return null

  if (showUpsell) {
    return (
      <div className="flex items-center">
        {/* mobile: lock icon button */}
        <Popover>
          <PopoverTrigger asChild>
            <IconButton
              icon={<Lock className="h-4 w-4" />}
              variant="tonal"
              size="sm"
              aria-label="Collaborate on this matter"
              className="sm:hidden"
              disabled={disabled}
            />
          </PopoverTrigger>
          <PopoverContent side="bottom" align="end" width="md">
            <UpsellPopoverContent onClick={handleUpsellClick} />
          </PopoverContent>
        </Popover>
        {/* desktop: text button with lock */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="tonal" color="neutral" size="sm" trailingIcon={<Lock className="h-4 w-4" />} className="hidden sm:flex" disabled={disabled}>
              Add
            </Button>
          </PopoverTrigger>
          <PopoverContent side="bottom" align="end" width="md">
            <UpsellPopoverContent onClick={handleUpsellClick} />
          </PopoverContent>
        </Popover>
      </div>
    )
  }

  const handleManageMembers = () => {
    if (!matter) return
    dispatch(MattersActions.openDialog({ dialogType: MatterDialogType.ManageMembers, matterId: matter.id }))
  }

  const canAdd = canEditMatterMembers(matter)

  return (
    <div className="flex items-center gap-2">
      {/* on desktop, show the avatar stack */}
      <div className="hidden sm:flex">
        <AvatarStack avatars={members} onClick={handleManageMembers} />
      </div>
      {canAdd && (
        <>
          {/* on mobile, show the icon button */}
          <IconButton
            icon={<AddCollaborator className="h-4 w-4" />}
            variant="tonal"
            size="sm"
            aria-label="Manage members"
            onClick={handleManageMembers}
            className="sm:hidden"
            disabled={disabled}
          />
          {/* on desktop, show the button with text */}
          <Button
            variant="tonal"
            color="neutral"
            size="sm"
            trailingIcon={<AddCollaborator className={`h-4 w-4 ${disabled ? 'text-true-gray-300' : 'text-brand-neutral-800'}`} />}
            onClick={handleManageMembers}
            className="hidden sm:flex"
            disabled={disabled}
          >
            Add
          </Button>
        </>
      )}
    </div>
  )
}
